import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  EmailOutlined as EmailIcon,
  Layers as FloorIcon,
  LocationOn as LocationIcon,
  MeetingRoom as CubeIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { IncidentRosterPerson } from '../../Types/Incident';
import PersonTypeChip from './PersonTypeChip';
import RosterSourceChip from './RosterSourceChip';
import IncidentDayBadgeInfo from './IncidentDayBadgeInfo';
import LobbyTrackVisitChip from './LobbyTrackVisitChip';
import LobbyTrackContextLine from './LobbyTrackContextLine';
import PhoneLink from './PhoneLink';
import { getCompanyDisplay, getInclusionReasonLabels } from '../../Utils/personHelpers';
import { getLobbyTrackActivityDisplay } from '../../Utils/lobbyTrackDisplay';

interface PersonMetadataProps {
  person: IncidentRosterPerson;
  /** Hide metadata lines (for collapsed/expanded states) */
  hidden?: boolean;
}

/**
 * Reusable person context block for roster rows.
 *
 * Line 1: Name + clickable phone (rendered by parent)
 * Line 2: email · company/department
 * Line 3: floor/location · PersonType chip · RosterSource chip
 * Line 4: Badge info (when data exists)
 */
const PersonMetadata: React.FC<PersonMetadataProps> = ({ person, hidden = false }) => {
  if (hidden) return null;

  const companyOrDept = getCompanyDisplay(person);
  const floorLabel = person.floor
    ? `Floor ${person.floor.replace(/^Floor\s+/i, '')}`
    : undefined;

  // Build line-2 segments: email · company/dept
  const hasLine2 = person.email || companyOrDept;
  const inclusionLabels = getInclusionReasonLabels(person);
  const lobbyDisplay = getLobbyTrackActivityDisplay(person.lobbyTrackActivity);
  const hasLine3 = floorLabel || person.location || person.personType || inclusionLabels.length > 0 || person.officeCubeNumber || lobbyDisplay.chipLabel;
  // Show badge line when any badge/source data exists (new or legacy fields)
  const hasBadgeLine = Boolean(
    person.lastIncidentDayBadgeActivity?.badgeActivityAtLocalDateTime
    || person.lobbyTrackActivity?.checkedInAtLocalDateTime
    || person.lastBadgeAtUtc
    || person.lobbyTrackCheckInAtUtc
    || person.rosterSource
    || person.isUnlisted
    || person.isAssignedToIncidentOffice
    || person.hasBadgeActivityToday !== undefined
  );

  return (
    <>
      {/* Line 2: Email · Company/Department */}
      {hasLine2 && (
        <Typography
          variant="caption"
          noWrap
          component="div"
          title={[person.email, companyOrDept].filter(Boolean).join(' \u2022 ')}
          sx={{ color: appColors.text.placeholder, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}
        >
          {person.email && (
            <Box
              component="a"
              href={`mailto:${person.email}`}
              aria-label={`Email ${person.displayName}`}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.25, textDecoration: 'none', color: 'inherit', flexShrink: 0, '&:hover': { color: appColors.primary.accent } }}
            >
              <EmailIcon sx={{ fontSize: 13, flexShrink: 0 }} />
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{person.email}</Box>
            </Box>
          )}
          {person.email && companyOrDept && (
            <Box component="span" sx={{ mx: 0.25 }}>{' \u2022 '}</Box>
          )}
          {companyOrDept && (
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {companyOrDept}
            </Box>
          )}
        </Typography>
      )}

      {/* Line 3: Chips — Floor, Location, Person type, Inclusion reasons */}
      {hasLine3 && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
          {floorLabel && (
            <Chip label={floorLabel} size="small" icon={<FloorIcon sx={{ fontSize: 12 }} />} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#E3F2FD', color: '#1565C0', '& .MuiChip-icon': { color: '#1565C0' } }} />
          )}
          {person.location && !floorLabel && (
            <Chip label={person.location} size="small" icon={<LocationIcon sx={{ fontSize: 12 }} />} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#E8F5E9', color: '#2E7D32', '& .MuiChip-icon': { color: '#2E7D32' } }} />
          )}
          <PersonTypeChip personType={person.personType} />
          {inclusionLabels.map((label) => (
            <RosterSourceChip key={label} rosterSource={label} />
          ))}
          {person.officeCubeNumber && (
            <Chip label={`Office ${person.officeCubeNumber}`} size="small" icon={<CubeIcon sx={{ fontSize: 12 }} />} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#EDE7F6', color: '#5E35B1', '& .MuiChip-icon': { color: '#5E35B1' } }} />
          )}
          {lobbyDisplay.chipLabel && lobbyDisplay.chipTone && (
            <LobbyTrackVisitChip label={lobbyDisplay.chipLabel} tone={lobbyDisplay.chipTone} />
          )}
        </Box>
      )}

      {/* Line 4: Badge info (skip when LobbyTrackContextLine will show instead) */}
      {hasBadgeLine && !lobbyDisplay.hasLobbyTrack && (
        <Box sx={{ mt: 0.25 }}>
          <IncidentDayBadgeInfo person={person} />
        </Box>
      )}

      {/* Line 5: Lobby Track visit context (signed-in/out details) */}
      {lobbyDisplay.hasLobbyTrack && (
        <Box sx={{ mt: 0.25 }}>
          <LobbyTrackContextLine activity={person.lobbyTrackActivity} />
        </Box>
      )}
    </>
  );
};

export default React.memo(PersonMetadata);
